import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import useDebounce from "../../../../hooks/UseDebounce";
import UseFetchTestShortCuts from "../../../../helper/UseFetchTestShortCuts";
import { useQuery } from "@tanstack/react-query";
import { getLabsList, getMedicalTest } from "../../../../connections/getApi";
import { showToast } from "../../../../utils/toastUtil";
import { postShortCode } from "../../../../connections/postApi";
import { deleteShortCode } from "../../../../connections/deleteApi";
import { updateTestShortCode } from "../../../../connections/putApi";
import { autoFocusOffWithOutBorder } from "../../../../style/PatinetFormStyle";
import { primaryBtnTeal } from "../../../../style/PatientDetailsStyle";
import CloseIcon from "@mui/icons-material/Close";
import Select, { components } from "react-select";
import CreatableSelect from "react-select/creatable";
import ArrowBackIosOutlinedIcon from "@mui/icons-material/ArrowBackIosOutlined";

const EditTestShortCode = ({
  refetch,
  onClose,
  shortCode,
  setShortCode,
  setIsEdit,
}) => {
  const [errors, setErrors] = useState(true);
  const [medicalTestRows, setMedicalTestRows] = useState([]);
  const [formState, setFormState] = useState({
    name: "",
  });
  const [isTyping, setIsTyping] = useState(false);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleChangeRow = (index, field, value, isNew) => {
    const updatedRows = [...medicalTestRows];
    updatedRows[index] = {
      ...updatedRows[index],
      [field]: value,
      ...(isNew !== undefined && { isNew }),
    };
    setMedicalTestRows(updatedRows);
  };

  const [medicalInput, setMedicalInput] = useState("");
  const handleMedicalChange = (val) => {
    setIsTyping(true);
    setMedicalInput(val);
  };

  const handleAddRow = () => {
    setMedicalTestRows((prev) => [
      ...prev,
      { medical_test_id: null, lab: null },
    ]);
  };

  const handleRemoveRow = (idx) => {
    const filtered = medicalTestRows.filter((_, i) => i !== idx);
    setMedicalTestRows(filtered);
  };

  const debounced = useDebounce(medicalInput, 700);

  const { data: medicalData, isFetching: loadingMedical } = useQuery({
    queryKey: ["medicalTests", debounced],
    queryFn: () => getMedicalTest(1, 10, null, debounced),
    enabled: !!debounced,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const [allMedicalTests, setAllMedicalTests] = useState([]);

  const medicalTests = medicalData?.payload?.medical_tests || [];
  const formattedMedicalOptions = medicalTests.map((t) => ({
    value: t.id,
    label: t.name,
  }));

  useEffect(() => {
    if (!formattedMedicalOptions?.length) return;

    setAllMedicalTests((prev) => {
      const merged = [...prev];

      formattedMedicalOptions.forEach((item) => {
        if (!prev.some((p) => p.value === item.value)) {
          merged.push(item);
        }
      });

      return merged;
    });
  }, [medicalData, formattedMedicalOptions]);

  useEffect(() => {
    if (!shortCode) return;
    // Pre-fill name
    setFormState({
      name: shortCode?.name || "",
    });

    // Convert edit tests into autocomplete-friendly objects
    const editTestOptions =
      shortCode?.medical_tests?.map((t) => ({
        value: t.id,
        label: t.name,
      })) || [];

    // Merge into allMedicalTests
    setAllMedicalTests((prev) => {
      const merged = [...prev];
      editTestOptions.forEach((item) => {
        if (!prev.some((p) => p.value === item.value)) {
          merged.push(item);
        }
      });
      return merged;
    });

    // Pre-fill rows
    setMedicalTestRows(
      shortCode?.medical_tests?.map((t) => ({
        medical_test_id: { value: t.id, label: t.name },
        lab: t?.lab ? { value: t.lab.id, label: t.lab.name } : null,
      })) || [],
    );
  }, []);

  const { data: labData, isFetching: loadingLabs } = useQuery({
    queryKey: ["labs"],
    queryFn: () => getLabsList("all", null, null),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const formattedLabOptions = (labData?.payload?.labs || []).map((l) => ({
    value: l.id,
    label: l.name,
  }));

  const handleUpdateShortCode = async () => {
    //  e.preventDefault();

    if (!formState.name) {
      showToast("Name is required", "ERROR");
      return;
    }

    for (let i = 0; i < medicalTestRows.length; i++) {
      const row = medicalTestRows[i];

      // Check if medical test is selected
      if (!row.medical_test_id) {
        showToast(
          `Row ${i + 1}: Please select or create a medical test`,
          "ERROR",
        );
        return;
      }

      // If it's a new test, check if lab is selected
      const isNewTest = typeof row.medical_test_id.value === "string";
      if (isNewTest && !row.lab) {
        showToast(
          `Row ${i + 1}: Please select a lab for the new test`,
          "ERROR",
        );
        return;
      }
    }

    // Process medical test rows
    const existingTestIds = [];
    const newMedicalTests = [];

    medicalTestRows.forEach((row) => {
      if (row.medical_test_id) {
        const testValue = row.medical_test_id.value;
        const testLabel = row.medical_test_id.label;
        const isNewTest = typeof testValue === "string";

        if (isNewTest) {
          const newTest = {
            name: testLabel,
            code: testValue.toUpperCase(),
          };
          // Add lab_id if lab is selected
          if (row.lab) {
            newTest.lab_id = row.lab.value;
          }
          newMedicalTests.push(newTest);
        } else {
          existingTestIds.push(testValue);
        }
      }
    });

    const payload = {
      medical_test_shortcode: {
        name: formState?.name,
        medical_test_ids: existingTestIds.map((id) => String(id)),
      },
      new_medical_tests: newMedicalTests,
    };

    try {
      const result = await updateTestShortCode(payload, shortCode?.id);
      showToast(
        result?.status?.message || result?.status?.status,
        result?.status?.status,
      );
      if (result?.status?.status === "SUCCESS") {
        onClose();
        setIsEdit(false);
        setFormState({});
        refetch();
      }
    } catch (err) {
      showToast(err.message, "ERROR");
    }
  };

  console.log(shortCode, "shortCode");

  return (
    <Box sx={{ width: "100%", padding: 2 }}>
      <Box mb={2} ml={-1} sx={{ alignItems: "center", display: "flex" }}>
        <IconButton
          sx={{ marginBottom: "0rem" }}
          onClick={() => {
            onClose();
            setMedicalTestRows([]);
            setFormState({});
          }}
        >
          <ArrowBackIosOutlinedIcon size="20px" sx={{ color: "#333333" }} />
        </IconButton>
        <Box>
          {" "}
          <Typography variant="heading2_14" color={"#333333"}>
            Back
          </Typography>
        </Box>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        position="sticky"
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Typography
            variant="title_medium"
            gutterBottom
            sx={{ fontSize: "20px" }}
          >
            Update Test Shortcode
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item sm={4}>
          <TextField
            value={formState?.name}
            fullWidth
            placeholder="Name"
            name="name"
            onChange={(e) => handleFormChange(e)}
            sx={{
              ...autoFocusOffWithOutBorder,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                height: "50px",
                padding: "9px !important",
                "& input": {
                  padding: "8px",
                  fontSize: "14px",
                },
                "& .MuiAutocomplete-popupIndicator": {
                  display: "none",
                },
              },
            }}
            error={!!errors["name"]}
            helperText={errors["name"]}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={1}>
        <Grid item xs={12}>
          <Typography variant="h6">Add Medical Tests</Typography>
        </Grid>

        {medicalTestRows.map((row, index) => (
          <React.Fragment key={index}>
            <Grid item xs={12} md={6}>
              <Box display="flex" gap={2} alignItems="center">
                <Typography>{index + 1}</Typography>
                <Box flex={1}>
                  <CreatableSelect
                    isLoading={loadingMedical}
                    options={allMedicalTests}
                    value={row?.medical_test_id}
                    onInputChange={handleMedicalChange}
                    onChange={(option) =>
                      handleChangeRow(
                        index,
                        "medical_test_id",
                        option,
                        option?.__isNew__,
                      )
                    }
                    placeholder="Choose medical test"
                    menuPortalTarget={document.body}
                    styles={{
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999, // Set your desired z-index here
                        fontFamily: "lato",
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 999999,
                      }),
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderColor: state.isFocused
                          ? "#2e37a4"
                          : "rgba(46, 55, 164, 0.1)",
                        boxShadow: state.isFocused
                          ? "0 0 0 1px #2e37a4"
                          : "none",
                        "&:hover": {
                          borderColor: "rgba(46, 55, 164, 0.1)",
                        },
                        fontFamily: "lato",
                        borderRadius: "10px",
                        fontSize: "14px",
                        minHeight: "50px",
                      }),
                      dropdownIndicator: (base, state) => ({
                        ...base,
                        transform: state.selectProps.menuIsOpen
                          ? "rotate(-180deg)"
                          : "rotate(0)",
                        transition: "250ms",
                        width: "35px",
                        height: "35px",
                      }),
                    }}
                  />
                </Box>
              </Box>
            </Grid>

            {row.isNew && (
              <Grid item xs={12} md={5}>
                <Select
                  options={formattedLabOptions}
                  value={row.lab}
                  onChange={(option) => handleChangeRow(index, "lab", option)}
                  placeholder="Choose lab"
                  menuPortalTarget={document.body}
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999, // Set your desired z-index here
                      fontFamily: "lato",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 999999,
                    }),
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused
                        ? "#2e37a4"
                        : "rgba(46, 55, 164, 0.1)",
                      boxShadow: state.isFocused ? "0 0 0 1px #2e37a4" : "none",
                      "&:hover": {
                        borderColor: "rgba(46, 55, 164, 0.1)",
                      },
                      fontFamily: "lato",
                      borderRadius: "10px",
                      fontSize: "14px",
                      minHeight: "50px",
                    }),
                    dropdownIndicator: (base, state) => ({
                      ...base,
                      transform: state.selectProps.menuIsOpen
                        ? "rotate(-180deg)"
                        : "rotate(0)",
                      transition: "250ms",
                      width: "35px",
                      height: "35px",
                    }),
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12} md={1}>
              <IconButton color="error" onClick={() => handleRemoveRow(index)}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </React.Fragment>
        ))}

        <Grid item xs={12} mt={2}>
          <Button
            variant="contained"
            color="primary"
            sx={{
              ...primaryBtnTeal,
            }}
            onClick={handleAddRow}
          >
            Add New Item
          </Button>
        </Grid>
      </Grid>

      <Box
        sx={{
          marginTop: 3,
          px: 1,
          width: "100%",
          display: "flex",
          justifyContent: "flex-end", // <-- This aligns the button to the right
        }}
      >
        <Button sx={{ ...primaryBtnTeal }} onClick={handleUpdateShortCode}>
          Update Shortcode
        </Button>
      </Box>
    </Box>
  );
};

export default EditTestShortCode;
